import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { MoreVertical, Crown, Shield, Trash2, Gift } from "lucide-react";

export interface UserStats {
  user_id: string;
  email: string;
  role: "admin" | "pro" | "free";
  forms_count: number;
  total_sessions: number;
  total_completed: number;
  total_pdf_downloads: number;
  monthly_ai_usage: number;
  created_at: string | null;
  plan_activated_at: string | null;
}

interface Props {
  users: UserStats[];
  onSetRole: (userId: string, role: "admin" | "pro" | "free") => void;
  onRequestDelete: (user: { id: string; email: string }) => void;
  onSendGift: (user: { id: string; email: string }) => void;
  sendingGiftTo?: string | null;
}

function formatDate(iso: string | null) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric" });
}

export default function UsersTab({ users, onSetRole, onRequestDelete, onSendGift, sendingGiftTo }: Props) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Todos os Usuários</CardTitle>
        <CardDescription>Gerencie usuários e seus planos</CardDescription>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Email</TableHead>
              <TableHead>Plano</TableHead>
              <TableHead>Conta criada</TableHead>
              <TableHead>Plano ativado</TableHead>
              <TableHead className="text-right">Formulários</TableHead>
              <TableHead className="text-right">Leads</TableHead>
              <TableHead className="text-right">Completados</TableHead>
              <TableHead className="text-right">Uso IA/Mês</TableHead>
              <TableHead className="w-[50px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.map((user) => (
              <TableRow key={user.user_id}>
                <TableCell className="font-medium">{user.email}</TableCell>
                <TableCell>
                  <Badge
                    variant={user.role === "admin" ? "secondary" : user.role === "pro" ? "default" : "outline"}
                  >
                    {user.role === "pro" && <Crown className="mr-1 h-3 w-3" />}
                    {user.role === "admin" && <Shield className="mr-1 h-3 w-3" />}
                    {user.role.toUpperCase()}
                  </Badge>
                </TableCell>
                <TableCell className="text-muted-foreground">{formatDate(user.created_at)}</TableCell>
                <TableCell className="text-muted-foreground">
                  {user.role === "free" ? "—" : formatDate(user.plan_activated_at)}
                </TableCell>
                <TableCell className="text-right">{user.forms_count}</TableCell>
                <TableCell className="text-right">{user.total_sessions}</TableCell>
                <TableCell className="text-right">{user.total_completed}</TableCell>
                <TableCell className="text-right">
                  {user.role === "free" ? `${user.monthly_ai_usage}/50` : user.monthly_ai_usage}
                </TableCell>
                <TableCell>
                  <div className="flex items-center justify-end gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    title="Enviar presente: 1 mês de Pro grátis"
                    disabled={sendingGiftTo === user.user_id}
                    onClick={() => onSendGift({ id: user.user_id, email: user.email })}
                  >
                    <Gift className="h-4 w-4 text-primary" />
                  </Button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => onSetRole(user.user_id, "pro")}>
                        <Crown className="mr-2 h-4 w-4 text-primary" />
                        Tornar Pro
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => onSetRole(user.user_id, "free")}>
                        Tornar Free
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => onSetRole(user.user_id, "admin")}>
                        <Shield className="mr-2 h-4 w-4 text-orange-500" />
                        Tornar Admin
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        className="text-destructive focus:text-destructive"
                        onClick={() => onRequestDelete({ id: user.user_id, email: user.email })}
                      >
                        <Trash2 className="mr-2 h-4 w-4" />
                        Excluir conta
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
